import firebase from 'firebase/compat/app';
import * as actionConstants from '../actionConstants';
import moment from 'moment-timezone';
import emailjs from '@emailjs/browser';
import { WEB_PAYMENT_URL } from '../environmentVariables';

//let functions = firebase.functions();

export const getAllCitations = (startDateInput, endDateInput, filters = {}) => {
  return async (dispatch, getState) => {
    dispatch({type: actionConstants.GETTING_ALL_CITATIONS})
    
    const user = getState().user.user;
    const db = firebase.firestore();
    const { clientId, locationId, limit } = filters || {};

    try {
      const addDateFilters = (query) => {
        if (startDateInput && endDateInput) {
          const startDateISO = moment.utc(startDateInput).toISOString();
          const endDateISO = moment.utc(endDateInput).toISOString();
          query = query.where('created_at', '>', startDateISO)
                       .where('created_at', '<', endDateISO);
        }
        return query;
      };

      const fetchTicketsForPath = async (cId, lId) => {
        let q = db
          .collection('clients')
          .doc(cId)
          .collection('locations')
          .doc(lId)
          .collection('tickets');
        q = addDateFilters(q).orderBy('created_at', 'desc');
        const snap = await q.get();
        const items = [];
        snap.forEach((doc) => {
          items.push({ id: doc.id, client: cId, location: lId, ...doc.data() });
        });
        return items;
      };

      let allTickets = [];

      // Regional admin: fetch for assigned locations (or a specific selected location) in parallel
      if (user.type === 'regional') {
        const assigned = user.assignedLocations || [];
        const targetLocations = locationId && locationId !== 'all' ? [locationId] : assigned;
        if (user.client && targetLocations.length > 0) {
          const chunks = await Promise.all(targetLocations.map(loc => fetchTicketsForPath(user.client, loc)));
          allTickets = chunks.flat();
        }
      // Client user (non-super, non-regional)
      } else if (user.type !== 'super') {
        const currentClient = getState().client.clients[0]?.id;
        if (currentClient) {
          if (locationId && locationId !== 'all') {
            allTickets = await fetchTicketsForPath(currentClient, locationId);
          } else {
            const locSnap = await db
              .collection('clients')
              .doc(currentClient)
              .collection('locations')
              .get();
            const chunks = await Promise.all(locSnap.docs.map(d => fetchTicketsForPath(currentClient, d.id)));
            allTickets = chunks.flat();
          }
        }
      } else {
        // Super admin
        if (clientId && clientId !== 'all') {
          if (locationId && locationId !== 'all') {
            allTickets = await fetchTicketsForPath(clientId, locationId);
          } else {
            const locSnap = await db
              .collection('clients')
              .doc(clientId)
              .collection('locations')
              .get();
            const chunks = await Promise.all(locSnap.docs.map(d => fetchTicketsForPath(clientId, d.id)));
            allTickets = chunks.flat();
          }
        } else {
          // No specific client filter: fall back to collectionGroup with date filter and a higher default limit
          let query = db.collectionGroup('tickets');
          query = addDateFilters(query).orderBy('created_at', 'desc');
          const snapshot = await query.get();
          snapshot.forEach(doc => {
            const data = doc.data();
            const pathParts = doc.ref.path.split('/');
            const cId = pathParts[1];
            const lId = pathParts[3];
            allTickets.push({ ...data, id: doc.id, client: cId, location: lId });
          });
        }
      }

      // Optional client-side final filters for safety
      if (clientId && clientId !== 'all') {
        allTickets = allTickets.filter(t => t.client === clientId);
      }
      if (locationId && locationId !== 'all') {
        allTickets = allTickets.filter(t => t.location === locationId);
      }

      // Sort by created_at desc
      allTickets.sort((a, b) => (a?.created_at || '') < (b?.created_at || '') ? 1 : -1);

      // Apply final limit AFTER server-side role filters have shaped the dataset
      if (limit && Number.isFinite(limit)) {
        allTickets = allTickets.slice(0, limit);
      }

      dispatch({type: actionConstants.GOT_ALL_CITATIONS, payload: allTickets})
      return allTickets;
      
    } catch (error) {
      console.error('Error fetching citations:', error);
      dispatch({type: actionConstants.GOT_ALL_CITATIONS, payload: []})
      return [];
    }
  }
}

/**
 * Fetch potential violations
 * Mirrors getAllCitations/getAllPayments role-based logic
 */
export const getAllViolations = (startDateInput, endDateInput, filters = {}) => {
  return async (dispatch, getState) => {
    dispatch({ type: actionConstants.GETTING_ALL_VIOLATIONS });

    const user = getState().user.user;
    const db = firebase.firestore();
    const { clientId, locationId, limit } = filters || {};

    try {
      const addDateFilters = (query) => {
        if (startDateInput && endDateInput) {
          const startDateISO = moment.utc(startDateInput).toISOString();
          const endDateISO = moment.utc(endDateInput).toISOString();
          query = query.where('scanned', '>', startDateISO)
                       .where('scanned', '<', endDateISO);
        }
        return query;
      };

      const fetchViolationsForPath = async (cId, lId) => {
        let q = db
          .collection('clients')
          .doc(cId)
          .collection('locations')
          .doc(lId)
          .collection('violations');
        q = addDateFilters(q).orderBy('scanned', 'desc');
        const snap = await q.get();
        const items = [];
        snap.forEach((doc) => {
          items.push({ id: doc.id, client: cId, location: lId, ...doc.data() });
        });
        return items;
      };

      let allViolations = [];

      if (user.type === 'regional') {
        const assigned = user.assignedLocations || [];
        const targetLocations = locationId && locationId !== 'all' ? [locationId] : assigned;
        if (user.client && targetLocations.length > 0) {
          const chunks = await Promise.all(targetLocations.map(loc => fetchViolationsForPath(user.client, loc)));
          allViolations = chunks.flat();
        }
      } else if (user.type !== 'super') {
        const currentClient = getState().client.clients[0]?.id;
        if (currentClient) {
          if (locationId && locationId !== 'all') {
            allViolations = await fetchViolationsForPath(currentClient, locationId);
          } else {
            const locSnap = await db
              .collection('clients')
              .doc(currentClient)
              .collection('locations')
              .get();
            const chunks = await Promise.all(locSnap.docs.map(d => fetchViolationsForPath(currentClient, d.id)));
            allViolations = chunks.flat();
          }
        }
      } else {
        // Super admin
        if (clientId && clientId !== 'all') {
          if (locationId && locationId !== 'all') {
            allViolations = await fetchViolationsForPath(clientId, locationId);
          } else {
            const locSnap = await db
              .collection('clients')
              .doc(clientId)
              .collection('locations')
              .get();
            const chunks = await Promise.all(locSnap.docs.map(d => fetchViolationsForPath(clientId, d.id)));
            allViolations = chunks.flat();
          }
        } else {
          // No specific client filter: use collectionGroup for violations
          let query = db.collectionGroup('violations');
          query = addDateFilters(query).orderBy('scanned', 'desc');
          const snapshot = await query.get();
          snapshot.forEach(doc => {
            const data = doc.data();
            const pathParts = doc.ref.path.split('/');
            const cId = pathParts[1];
            const lId = pathParts[3];
            allViolations.push({ ...data, id: doc.id, client: cId, location: lId });
          });
        }
      }

      // Optional client-side filters for safety
      if (clientId && clientId !== 'all') {
        allViolations = allViolations.filter(v => v.client === clientId);
      }
      if (locationId && locationId !== 'all') {
        allViolations = allViolations.filter(v => v.location === locationId);
      }

      // Sort by scanned desc
      allViolations.sort((a, b) => (a?.scanned || '') < (b?.scanned || '') ? 1 : -1);

      if (limit && Number.isFinite(limit)) {
        allViolations = allViolations.slice(0, limit);
      }

      // Look up vehicle details (make/model/color) from /vehicles for each violation.vehicle
      const vehicleIds = [...new Set(allViolations.map(v => v.vehicle).filter(Boolean))];
      let vehicleMap = {};

      if (vehicleIds.length > 0) {
        const chunks = [];
        const chunkSize = 10; // Firestore 'in' query limit
        for (let i = 0; i < vehicleIds.length; i += chunkSize) {
          chunks.push(vehicleIds.slice(i, i + chunkSize));
        }

        const snapshots = await Promise.all(
          chunks.map(chunk =>
            db
              .collection('vehicles')
              .where(firebase.firestore.FieldPath.documentId(), 'in', chunk)
              .get()
          )
        );

        snapshots.forEach(snap => {
          snap.forEach(doc => {
            vehicleMap[doc.id] = doc.data();
          });
        });
      }

      const enrichedViolations = allViolations.map(v => {
        const vehicle = v.vehicle && vehicleMap[v.vehicle] ? vehicleMap[v.vehicle] : {};
        return {
          ...v,
          make: vehicle.car_make || null,
          model: vehicle.car_type || null,
          color: vehicle.car_color || null,
        };
      });

      dispatch({ type: actionConstants.GOT_ALL_VIOLATIONS, payload: enrichedViolations });
      return enrichedViolations;
    } catch (error) {
      console.error('Error fetching violations:', error);
      dispatch({ type: actionConstants.GOT_ALL_VIOLATIONS, payload: [] });
      return [];
    }
  };
};

/**
 * Delete a single potential violation
 */
export const deleteViolation = (clientId, locationId, violationId) => {
  return async (dispatch, getState) => {
    try {
      const db = firebase.firestore();
      await db
        .collection('clients')
        .doc(clientId)
        .collection('locations')
        .doc(locationId)
        .collection('violations')
        .doc(violationId)
        .delete();

      // After deletion, refetch current violations using existing filters from state, if available
      const { dateRangeFilter, clientFilter, locationFilter } = getState().dashboard || {};
      const startDate = dateRangeFilter?.startDate || null;
      const endDate = dateRangeFilter?.endDate || null;

      await getAllViolations(
        startDate,
        endDate,
        {
          clientId: clientFilter || 'all',
          locationId: locationFilter || 'all',
        }
      )(dispatch, getState);

      return true;
    } catch (error) {
      console.error('Error deleting violation:', error);
      return false;
    }
  };
};

export const getAllCitationsByViolator = (startDateInput, endDateInput) => {
  return async (dispatch, getState) => {
    dispatch({type: actionConstants.GETTING_ALL_CITATIONS})
    let citations
    let query
    let startDate = moment(startDateInput).toISOString()
    let endDate = moment(endDateInput).toISOString()
    const getAllCitationsQuery = firebase.functions().httpsCallable('bigQueryRawSql')
    
    const user = getState().user.user;
    
    if (user.type === 'super') {
      query = `
        SELECT 
          JSON_VALUE(t.data, '$.license_plate') as license_plate,
          ARRAY_AGG(
            STRUCT(
              t.data as data, 
              JSON_VALUE(t.path_params, '$.clientId') as clientId, 
              JSON_VALUE(t.path_params, '$.locationId') as locationId, 
              t.document_id as id
            )
          ) as citations
        FROM \`firestore_tickets.tickets_raw_latest\` t 
        WHERE 1=1 `
    } else if (user.type === 'regional') {
      // For regional admins, filter by their assigned locations
      if (!user.assignedLocations || user.assignedLocations.length === 0) {
        dispatch({type: actionConstants.GOT_ALL_CITATIONS, payload: []})
        return []
      }
      
      const locationFilter = user.assignedLocations.map(locId => `'${locId}'`).join(',')
      query = `
        SELECT 
          JSON_VALUE(t.data, '$.license_plate') as license_plate,
          ARRAY_AGG(
            STRUCT(
              t.data as data, 
              JSON_VALUE(t.path_params, '$.clientId') as clientId, 
              JSON_VALUE(t.path_params, '$.locationId') as locationId, 
              t.document_id as id
            )
          ) as citations
        FROM \`firestore_tickets.tickets_raw_latest\` t 
        WHERE JSON_VALUE(t.path_params, '$.clientId') = '${user.client}' AND JSON_VALUE(t.path_params, '$.locationId') IN (${locationFilter}) `
    } else {
      query = `
        SELECT 
          JSON_VALUE(t.data, '$.license_plate') as license_plate,
          ARRAY_AGG(
            STRUCT(
              t.data as data, 
              JSON_VALUE(t.path_params, '$.clientId') as clientId, 
              JSON_VALUE(t.path_params, '$.locationId') as locationId, 
              t.document_id as id
            )
          ) as citations
        FROM \`firestore_tickets.tickets_raw_latest\` t 
        WHERE JSON_VALUE(t.path_params, '$.clientId') = '${getState().client.clients[0].id}' `
    }
    
    if (startDateInput && endDateInput) {
      query = query + ' AND JSON_VALUE(t.data, "$.created_at") > "' + startDate + '" AND JSON_VALUE(t.data, "$.created_at") < "' + endDate + '"'
    }
    
    // Add GROUP BY clause for license plate
    query = query + ' GROUP BY license_plate'
    
    citations = await getAllCitationsQuery({query}).then(result => result).catch(error => console.log(error))
    
    let formattedCitations
    let finalCitations = []
    
    if (citations?.data) {
      formattedCitations = JSON.parse(citations.data)
    }
    
    if (formattedCitations?.length > 0) {
      formattedCitations[0].forEach(group => {
        const licensePlate = group.license_plate
        const citationsForLicense = group.citations.map(citation => {
          const rowData = JSON.parse(citation.data)
          return {
            ...rowData, 
            client: citation.clientId, 
            location: citation.locationId, 
            id: citation.id
          }
        })
        
        finalCitations.push({
          license_plate: licensePlate,
          citations: citationsForLicense,
          client: citationsForLicense[0].client,
          total_count: citationsForLicense.length
        })
      })
    }
    
    dispatch({type: actionConstants.GOT_ALL_CITATIONS, payload: finalCitations})
    return finalCitations
  }
}

export const getAllPayments = (startDateInput, endDateInput) => {
  return async (dispatch, getState) => {
    dispatch({type: actionConstants.GETTING_ALL_PAYMENTS})
    let payments
    let query
    // Defensive normalization: callers should pass startOfDay/endOfDay already, but
    // if a raw 00:00-local Date slips through, endOf('day') prevents the +1-day bug
    // where everything after midnight local on the chosen end day got dropped.
    let startDate = moment(startDateInput).startOf('day').toISOString()
    let endDate = moment(endDateInput).endOf('day').toISOString()
    const getAllPaymentsQuery = firebase.functions().httpsCallable('bigQueryRawSql')
    
    const user = getState().user.user;
    
    if (user.type === 'super') {
      query = "SELECT t.data as data, JSON_VALUE(t.path_params, '$.clientId') as clientId, JSON_VALUE(t.path_params, '$.locationId') as locationId, t.document_id as id  FROM `firestore_tickets.tickets_raw_latest` t where 1=1 "
    } else if (user.type === 'regional') {
      // For regional admins, filter by their assigned locations
      if (!user.assignedLocations || user.assignedLocations.length === 0) {
        dispatch({type: actionConstants.GOT_ALL_PAYMENTS, payload: []})
        return []
      }
      
      const locationFilter = user.assignedLocations.map(locId => `'${locId}'`).join(',')
      query = `SELECT t.data as data, JSON_VALUE(t.path_params, '$.clientId') as clientId, JSON_VALUE(t.path_params, '$.locationId') as locationId, t.document_id as id  FROM \`firestore_tickets.tickets_raw_latest\` t where JSON_VALUE(t.path_params, '$.clientId') = '${user.client}' AND JSON_VALUE(t.path_params, '$.locationId') IN (${locationFilter}) `
    } else {
      query = "SELECT t.data as data, JSON_VALUE(t.path_params, '$.clientId') as clientId, JSON_VALUE(t.path_params, '$.locationId') as locationId, t.document_id as id  FROM `firestore_tickets.tickets_raw_latest` t where JSON_VALUE(t.path_params, '$.clientId') = '" + getState().client.clients[0].id + "' "
    }
    if (startDateInput && endDateInput) {
      query = query + ' AND JSON_VALUE(t.data, "$.payment_on") >= "' + startDate + '" AND JSON_VALUE(t.data, "$.payment_on") <= "' + endDate + '"'
    }
    payments = await getAllPaymentsQuery({query}).then(result => result).catch(error => console.log(error))
    let formattedPayments
    let finalPayments = []
    if (payments?.data) {
      formattedPayments = JSON.parse(payments.data)
    }
    if (formattedPayments?.length > 0) {
      formattedPayments[0].forEach(row => {
        let rowData = JSON.parse(row.data)
        finalPayments.push({...rowData, client: row.clientId, location: row.locationId, id: row.id})
        // if (!rowData.dispute || rowData.dispute?.status !== 'accepted') {
        //   finalCitations.push({...rowData, client: row.clientId, location: row.locationId, id: row.id})
        // }
      })
    }
    dispatch({type: actionConstants.GOT_ALL_PAYMENTS, payload: finalPayments})
    return finalPayments
  }
}

/**
 * Get single citation
 * 
 * @param {*} citation 
 * @returns 
 */
export const getSingleCitation = (violationNumber) => {
  return async (dispatch, getState) => {
    return firebase
      .firestore()
      .collectionGroup('tickets')
      .where('violation_number', '==', violationNumber)
      .get()
      .then(querySnapshot => {
        if (querySnapshot.size > 0) {
          let doc = querySnapshot.docs[0]
          let client = doc.ref.parent.parent.parent.parent.id
          dispatch({ type: actionConstants.SET_CITATION, payload: {id: doc.id, ...doc.data(), location: doc.data().location, client: client}})
        }
      })
  }
}

/**
 * Mark ticket/citation as paid
 * 
 * @param {*} citation 
 * @param {*} paymentMethod - The payment method selected (check, cash, ach, other)
 * @returns 
 */
export const markAsPaid = (citation, paymentMethod = 'other') => {
  return async (dispatch, getState) => {
    const userId = getState().user.user?.id;
    const payment = {
      type: paymentMethod,
      amount: parseFloat(citation.violations_total).toFixed(2),
      reference: "Manual payment",
    };
    if (userId) payment.user = userId;

    return firebase
      .firestore()
      .collection('clients')
      .doc(citation.client)
      .collection('locations')
      .doc(citation.location)
      .collection('tickets')
      .doc(citation.id)
      .update({
        status: "closed",
        payment_on: moment.utc().toISOString(),
        payment,
      })
      .then(doc => {})
      .catch(error => console.log(error))
  }
}

/**
 * Accept disputed citation
 * 
 * @param {*} citation 
 * @returns 
 */
export const acceptDispute = (citation, accept_note = null) => {
  return (dispatch, getState) => {
    return firebase
      .firestore()
      .collection('clients')
      .doc(citation.client)
      .collection('locations')
      .doc(citation.location)
      .collection('tickets')
      .doc(citation.id)
      .update({
        dispute: {
          ...citation.dispute,
          accepted_on: moment().utc().toISOString(),
          accepted_by: getState().user.user.id,
          accepted_note: accept_note,
          status: 'accepted'
        },
        total: 0,
        status: 'closed'
      })
      .then(() => {
        dispatch(getAllCitations())
        // TODO: EmailJS template 'template_xwyylde' needs to be updated to include the accept_note parameter
        // Currently the note is being passed but not displayed in the email
        emailjs.send("service_cghbh4g","template_xwyylde",{
          violation_number: citation.violation_number,
          to_email: citation.dispute.email,
          accept_note: accept_note || '',
        }, 'Bdnrcb9nkMG24q1Uv');
      })
    }
}

/**
 * Void a citation
 * 
 * @param {*} citation 
 * @returns 
 */
export const voidCitation = (citation, void_note = null) => {
  return (dispatch, getState) => {
    let dispute = null
    if (citation?.dispute) {
      dispute = {
        ...citation.dispute,
        accepted_on: moment().utc().toISOString(),
        accepted_by: getState().user.user.id,
        status: 'accepted'
      }
    }
    return firebase
      .firestore()
      .collection('clients')
      .doc(citation.client)
      .collection('locations')
      .doc(citation.location)
      .collection('tickets')
      .doc(citation.id)
      .update({
        dispute: dispute || null,
        total: 0,
        status: 'void',
        voided_on: moment().utc().toISOString(),
        voided_by: getState().user.user.id,
        voided_note: void_note
      })
      .then(() => {
        dispatch(getAllCitations())
      })
    }
}

/**
 * Reject disputed citation
 * 
 * @param {*} citation 
 * @param {*} adjustmentNote 
 * @returns 
 */
export const rejectDispute = (citation, adjustmentNote) => {
  return (dispatch, getState) => {
    return firebase
      .firestore()
      .collection('clients')
      .doc(citation.client)
      .collection('locations')
      .doc(citation.location)
      .collection('tickets')
      .doc(citation.id)
      .update({
        dispute: {
          ...citation.dispute,
          rejected_on: moment().utc().toISOString(),
          rejected_by: getState().user.user.id,
          adjustment_note: adjustmentNote || null,
          status: 'rejected'
        }
      })
      .then(() => {
        dispatch(getAllCitations())
        emailjs.send("service_cghbh4g","template_361nh82",{
          violation_number: citation.violation_number,
          to_email: citation.dispute.email,
          balance: citation.violations_total,
          adjustment_note: adjustmentNote || '',
          link: WEB_PAYMENT_URL + '/violations/' + citation.vehicle,
        }, 'Bdnrcb9nkMG24q1Uv');
      })
    
  }
}

/**
 * 
 * @param {*} citation 
 * @param {*} newAmount 
 * @param {*} adjustmentNote 
 * @returns 
 */
export const adjustDispute = (citation, newAmount, adjustmentNote) => {
  if (!newAmount || isNaN(newAmount)) {
    newAmount = 0
  }
  
  return (dispatch, getState) => {
    const location = getState().location.locations.find(location => location.id === citation.location)
    return firebase
      .firestore()
      .collection('clients')
      .doc(citation.client)
      .collection('locations')
      .doc(citation.location)
      .collection('tickets')
      .doc(citation.id)
      .update({
        dispute: {
          ...citation.dispute,
          adjusted_on: moment().utc().toISOString(),
          adjusted_by: getState().user.user.id,
          original_amount: citation.violations_total,
          adjusted_discount: parseFloat(citation.violations_total) - parseFloat(newAmount),
          status: 'adjusted',
          adjustment_note: adjustmentNote || null
        },
        early_bird_percentage: 0,
        total: parseFloat(parseFloat(newAmount)).toFixed(2),
        penalty_start_at: location?.penalty?.days ? moment.utc().add(location.penalty.days, "days").toISOString() : null
      })
      .then(() => {
        dispatch(getAllCitations())
        emailjs.send("service_cghbh4g","template_hiucmu3",{
          violation_number: citation.violation_number,
          to_email: citation.dispute.email,
          adjusted_discount: parseFloat(parseFloat(citation.violations_total) - parseFloat(newAmount)).toFixed(2),
          new_balance: parseFloat(newAmount).toFixed(2),
          adjustment_note: adjustmentNote || '',
          link: WEB_PAYMENT_URL + '/violations/' + citation.vehicle,
        }, 'Bdnrcb9nkMG24q1Uv');
      })
  }
}

/**
 * Clear citation
 * 
 * @param {*} citation 
 * @returns 
 */
export const clearCitation = () => {
  return async (dispatch, getState) => {
    dispatch({ type: actionConstants.SET_CITATION, payload: null})
  }
}

/**
 * Add a note to a citation
 * @param {*} citation - citation object
 * @param {string} noteText - the note text
 * @param {object} user - user object (should have id and name)
 */
export const addCitationNote = (citation, noteText, user) => {
  return async (dispatch, getState) => {
    const newNote = {
      text: noteText,
      user: user.name,
      userId: user.id,
      created_at: moment.utc().toISOString()
    };
    // Get current notes or empty array
    const currentNotes = citation.notes || [];
    const updatedNotes = [newNote, ...currentNotes];
    await firebase
      .firestore()
      .collection('clients')
      .doc(citation.client)
      .collection('locations')
      .doc(citation.location)
      .collection('tickets')
      .doc(citation.id)
      .update({ notes: updatedNotes });
    // Locally update citation and dispatch
    const updatedCitation = { ...citation, notes: updatedNotes };
    dispatch({ type: actionConstants.SET_CITATION, payload: updatedCitation });
  };
};

/**
 * Respond to a dispute (send email and log activity)
 * @param {*} citation 
 * @param {string} message - The message to send
 * @returns 
 */
export const respondDispute = (citation, message) => {
  return (dispatch, getState) => {
    const user = getState().user.user;
    return firebase
      .firestore()
      .collection('clients')
      .doc(citation.client)
      .collection('locations')
      .doc(citation.location)
      .collection('tickets')
      .doc(citation.id)
      .update({
        dispute: {
          ...citation.dispute,
          responded_on: moment().utc().toISOString(),
          responded_by: user.id,
          responded_message: message,
          status: 'responded'
        }
      })
      .then(() => {
        dispatch(getAllCitations());
        emailjs.send("service_cghbh4g","template_responddispute",{
          violation_number: citation.violation_number,
          to_email: citation.dispute.email,
          respond_message: message || '',
        }, 'Bdnrcb9nkMG24q1Uv');
      });
  };
};
